// Portfolio NAV over time for the investor dashboard. Fed by
// /api/portfolio/nav (summed across active strategies, carry-forward).
// Owns its own range toggle (24h / 7d / 30d / All). Light-themed to
// match the customer canvas.
import { useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { usePortfolioNav } from '../../hooks/useApi';
import type { PortfolioRange } from '../../lib/api';
import {
  cn,
  formatCurrency,
  formatDateShort,
  formatDateTime,
  formatTimeShort,
} from '../../lib/utils';

const RANGES: { key: PortfolioRange; label: string }[] = [
  { key: '24h', label: '24h' },
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' },
  { key: 'all', label: 'All' },
];

const AREA = 'hsl(160 84% 39%)'; // emerald
const GRID = 'hsl(214 32% 91%)';
const TEXT = 'hsl(215 16% 47%)';
const BASELINE = 'hsl(215 16% 60%)';

function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: { value: number }[];
  label?: number;
}) {
  if (!active || !payload?.length || label === undefined) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm shadow-lg">
      <p className="mb-1 text-slate-500">{formatDateTime(label)}</p>
      <p className="font-semibold tabular-nums text-slate-900">
        NAV: {formatCurrency(payload[0]!.value)}
      </p>
    </div>
  );
}

export function PortfolioNavChart({
  height = 300,
  defaultRange = '7d',
  showAll = true,
}: {
  height?: number;
  defaultRange?: PortfolioRange;
  showAll?: boolean;
}) {
  const [range, setRange] = useState<PortfolioRange>(defaultRange);
  const { data, isLoading } = usePortfolioNav(range);
  const ranges = showAll ? RANGES : RANGES.filter((r) => r.key !== 'all');

  const series = data?.timeSeries ?? [];
  const intraday = range === '24h';
  const latest = series.length ? series[series.length - 1]!.navUsd : null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">
            Performance proof
          </p>
          <h3 className="text-lg font-semibold text-slate-900">Strategy NAV</h3>
        </div>
        <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1">
          {ranges.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={cn(
                'rounded-md px-3 py-1 text-sm font-medium transition-colors',
                range === r.key
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-900',
              )}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {latest !== null && (
        <p className="mb-2 text-right text-sm font-semibold tabular-nums text-emerald-600">
          {formatCurrency(latest)} NAV
        </p>
      )}

      <ResponsiveContainer width="100%" height={height}>
        {isLoading || series.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-slate-400">
            {isLoading ? 'Loading…' : 'No data yet'}
          </div>
        ) : (
          <AreaChart data={series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="pfNavGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={AREA} stopOpacity={0.18} />
                <stop offset="95%" stopColor={AREA} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
            <XAxis
              dataKey="ts"
              type="number"
              domain={['dataMin', 'dataMax']}
              tickFormatter={intraday ? formatTimeShort : formatDateShort}
              tick={{ fill: TEXT, fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              minTickGap={48}
            />
            <YAxis
              tick={{ fill: TEXT, fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v: number) => `$${v.toFixed(0)}`}
              width={56}
              domain={['auto', 'auto']}
            />
            <Tooltip content={<ChartTooltip />} cursor={{ stroke: GRID }} />
            {data?.deployedCapitalUsd ? (
              <ReferenceLine
                y={data.deployedCapitalUsd}
                stroke={BASELINE}
                strokeDasharray="4 4"
                label={{
                  value: `deployed ${formatCurrency(data.deployedCapitalUsd)}`,
                  position: 'insideTopRight',
                  fontSize: 10,
                  fill: BASELINE,
                }}
              />
            ) : null}
            <Area
              type="monotone"
              dataKey="navUsd"
              stroke={AREA}
              strokeWidth={2}
              fill="url(#pfNavGradient)"
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0 }}
            />
          </AreaChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}
